/* eslint-disable consistent-return */
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import React from 'react';
import { Container, Row, ThemeProvider } from 'react-bootstrap';
import { Switch } from 'react-router-dom';
import BlockPage from '../../shared/components/block-page';
import ConfigRoute from '../../shared/components/config-route';
import HelmetIcon from '../../shared/components/helmet-icon';
import { RequestStatus } from '../../shared/enums/request-status';
import {
  OnboardingSteps,
  ProfileProgressSteps,
  UserSettingCode,
} from '../../shared/types/user-setting';
import {
  AuthHelper,
  getDemoAccountEntryPoint,
  getIsRequestSucceeded,
  removeDemoAccountEntryPoint,
  setSessionExpireHandler,
} from '../../shared/utils';
import { Permissions } from '../../shared/utils/access-control/enums/permissions';
import hasPermission from '../../shared/utils/access-control/has-permission';
import { planError } from '../../shared/utils/errors/plan-permission-error/plan-error';
import {
  executeOnErrorWithErrorCheck,
  executeOnRequestStatusWithPrevStatusCheck,
} from '../../shared/utils/execute-on-request-status';
import { handleOnboarding } from '../../shared/utils/handle-onboarding-routing';
import {
  destroyChatSupport,
  initializeIntercom,
} from '../../shared/utils/helpscout-handlers';
import { initializeThirdPartyIntegrations } from '../../shared/utils/initialize-third-party-integrations';
import hasResource from '../../shared/utils/access-control/has-resource';
import { ResourceIdentifiers } from '../../shared/utils/access-control/enums/resource-identifiers';
import { getNewSequenceName } from '../sequence/components/sequence-list/helper';
import HelpscoutBeaconEvents from '../../shared/enums/helpscout-beacon-events';
import { Source } from '../../shared/enums/source';
import ErrorBoundaryWrapper from '../../shared/components/error-boundary-wrapper';
import { PaymentActionType } from '../../shared/design-system/components/atoms/banner/banner';
import toaster, { Theme } from '../../shared/toaster';
import { TrialExtendSource } from '../../shared/enums/trial-extend-source';
import growthHub from '../growth-hub';
import AgencyPortal from '../agency-portal';
import OnboardingModal from '../../shared/components/onboarding-modal';
import Alert from '../../shared/design-system/components/alert';
import { accessibleOnClick } from '../../shared/utils/accessible-on-click';
import VerifyEmailCallback from '../auth/components/verify-email-callback';
import { openIntercomHome } from '../../shared/utils/open-chat-support';
import Tasks from '../tasks';
import { PageHeaderAlertTypes } from '../../shared/utils/page-header-alert-types';
import {
  redirectWithRefresh,
  redirectWithoutRefresh,
} from '../../shared/utils/redirect';
import { SubscriptionPlans } from '../../shared/utils/subscription-plans';
import EmailWarmup from '../email-warmup';
import leads from '../leads';
import V2Lead from '../v2-lead';
import MailboxEmails from '../mailbox-emails';
import Prospect from '../prospect';
import Dialer from '../dialer';
import Reports from '../reports';
import Sequence from '../sequence';
import { getIsRequestPending } from '../sequence/helpers/helper';
import Setting from '../settings';
import Templates from '../templates';
import Billing from '../billing';
import UnifiedInbox from '../unified-inbox';
import Header from './components/header';
import Sidebar from './components/sidebar';
import WatchVideoModal from './components/watch-video-modal';
import DemoAccountBanner from './components/demo-account-banner';
import type { IProps, IState } from './home-container';
import { isPlanDowngraded } from './utils/plan-transition';
import AgencyClient from '../agency-client-management/agency-client';
import { getDemoAccountEnteredTimestamp, storeUserEmail } from './utils/helper';
import {
  isWhitelabelWithoutClientView,
  hideSideBarTabs,
  isWhitelabelWithClientView,
  leadFinderViewOnly,
  hasV2LeadFinderAccess,
} from '../../shared/utils/user-details';
import { getCurrentRoute } from '../../shared/utils/get-current-route';
import {
  initializeCustomScript,
  removeCustomScript,
} from '../../shared/utils/initialize-custom-script';
import SessionExpireModal from './components/header/components/session-expire-modal';
import {
  clearAgencyClientRedirectURLOnLogout,
  getAgencyClientRedirectURLOnLogout,
} from '../auth/components/agency-client-login/helper';
import EmailAccounts from '../email-accounts';
import Domains from '../domains';
import InframailIps from '../inframail-ips';
import { Routes } from '../../shared/routes';
import { getSubscriptionRenewalStatus } from '../domains/helpers/subscription-renewal.api';
import inboxRadar from '../inbox-radar';
import { AnalyticsEvents } from '../../shared/enums/analytics';
import FeedbackModal from './modals/feedback-modal';
import { handleDemoFeedbackModal } from './utils/demo-feedback-helper';
import EmailVerifier from '../email-verifier';
import { handlePaymentAction } from './utils/payment-action';
import ThirdPartyIntegrations from '../../shared/components/third-party-integrations';
import MaintenancePage from '../../shared/components/maintenance-page';
import DialerManager from '../../shared/components/dialer/dialer-manager';

class Home extends React.Component<IProps, IState> {
  private unlisten: any;

  private originalFavicon: null;

  private hasInitializedDialer: boolean;

  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      showWatchVideoModal: false,
      showGettingStarted: false,
      showHelpscoutWelcomeMessage: false,
      isViewSequenceBtnClicked: false,
      showOnboardingModal: false,
      willRedirectToLeadFinder: false,
      sessionExpire: false,
    };
    this.originalFavicon = null;
    this.hasInitializedDialer = false;

    this.updateWatchVideoProfileProgressStep = this.updateWatchVideoProfileProgressStep.bind(
      this,
    );
    this.updateConnectEmailProfileProgressStep = this.updateConnectEmailProfileProgressStep.bind(
      this,
    );
    this.showHideWatchVideoModal = this.showHideWatchVideoModal.bind(this);
    this.showHideGettingStarted = this.showHideGettingStarted.bind(this);
    this.showHideHelpscoutWelcomeMessage = this.showHideHelpscoutWelcomeMessage.bind(
      this,
    );
    this.showHideOnboardingModal = this.showHideOnboardingModal.bind(this);
    this.createSequence = this.createSequence.bind(this);
    this.handleHelpscoutWelcomeMessage = this.handleHelpscoutWelcomeMessage.bind(
      this,
    );
    this.onViewSampleSequence = this.onViewSampleSequence.bind(this);
    this.initializeHelpScoutChatSupport = this.initializeHelpScoutChatSupport.bind(
      this,
    );
    // this.initializeCelloReferral = this.initializeCelloReferral.bind(this);
    this.handleSendVerificationLinkRequest = this.handleSendVerificationLinkRequest.bind(
      this,
    );
    // this.unlisten = React.createRef();
  }

  // eslint-disable-next-line react/sort-comp
  redirectSpamUrl = () => {
    window.open(
      'https://docs.saleshandy.com/en/articles/8743773-troubleshooting-didn-t-receive-your-verification-email',
      '_blank',
    );
  };

  componentDidMount() {
    const {
      sendGetUserSettingsRequest,
      sendGetAgencyConfigRequest,
      email,
      location,
      sendCompareAuthTokenRequest,
      getMyProfileRequestStatus,
      history,
    } = this.props;

    // Check if hideSideBarTabs is true and redirect to /reports/sequence
    if (hideSideBarTabs()) {
      history.push('/reports/sequence');
      return;
    }
    if (leadFinderViewOnly()) {
      history.push('/leads#people');
      return;
    }

    sendGetUserSettingsRequest();

    if (getMyProfileRequestStatus === RequestStatus.Succeeded) {
      const agencyConfigPayload = {
        baseUrl: window.location.origin,
        email,
      };
      sendGetAgencyConfigRequest(agencyConfigPayload);
    }

    // Store the original favicon URL
    const originalFavicon: any = document.querySelector("link[rel*='icon']");
    if (originalFavicon) {
      this.originalFavicon = originalFavicon.href;
    }

    // this.unlisten.current = history.listen((locationParam) => {
    //   if (isPlanDowngraded(this.getPlanTransitionDetails())) {
    //     if (
    //       !hasRedirectToBlockPage(locationParam.pathname) &&
    //       locationParam.pathname !== '/login' &&
    //       locationParam.pathname !== '/signup' &&
    //       history.location.pathname !== '/block'
    //     ) {
    //       history.push('/block');
    //     }
    //   }
    // });

    const params = new URLSearchParams(location.search);

    const authToken = params.get('authToken');
    if (authToken) {
      sendCompareAuthTokenRequest({
        authToken,
      });
    }

    setSessionExpireHandler(this.handleSessionExpire);

    // Add route listener to handle manual URL changes
    this.unlisten = history.listen((locationParam) => {
      const { pathname } = locationParam;

      if (hideSideBarTabs()) {
        // Redirect to reports if user navigates elsewhere
        if (!pathname.startsWith('/reports/sequence')) {
          history.push('/reports/sequence');
          return;
        }
      }

      if (leadFinderViewOnly()) {
        // Redirect to leads#people if user navigates elsewhere or changes hash
        if (
          !pathname.startsWith('/leads') ||
          locationParam.hash !== '#people'
        ) {
          history.push(`/leads${locationParam.search}#people`);
        }
      }
    });

    window.addEventListener('pagehide', this.handleTabClose);
  }

  componentDidUpdate(prevProps) {
    const {
      sendGetUserSettingsRequest,
      sendGetUserSubscriptionMetadataRequest,
      sendGetPostLoadMetaRequest,
      getUserSettingsRequestStatus,
      getUserSubscriptionMetadataRequestStatus,
      getUserSubscriptionMetadataRequestError,
      resetGetUserSubscriptionMetadataRequest,
      getSampleSequenceRequestStatus,
      updateProfileProgressRequestStatus,
      location,
      createSequenceFromGettingStartedRequestStatus,
      gettingStatedCreatedSequenceId,
      hasUserConnectedEmailAccount,
      handleHasUserConnectedEmailAccount,
      compareAuthTokenRequestStatus,
      shouldLogoutUser,
      resetCompareAuthTokenRequest,
      history,
      agencyConfig,
      sendGetAgencyConfigRequest,
      shAccountId,
      email,
      firstName,
      lastName,
      client,
      trackingId,
      extendTrialPeriodRequestStatus,
      extendTrialPeriodRequestMessage,
      onBoardingRequestStatus,
      onBoardingRequestError,
      resendVerificationEmailRequestStatus,
      resendVerificationEmailRequestError,
      outcomeChangeRequestStatus,
      outcomeChangeRequestMessage,
      getAgencyConfigRequestStatus,
      isGettingStartedVideoModalShow,
      isDemoAccount,
      leadFinder,
      isDialerFeatureEnabled,
      isDemoeAccount,
      createTwillioVoiceTokenRequest,
      sendMarkUnseenMissedCallCountRequest,
      sendGetMyPhoneNumberListRequest,
    } = this.props;

    // Check if hideSideBarTabs is true and redirect to /reports/sequence
    if (
      hideSideBarTabs() &&
      !location.pathname.startsWith('/reports/sequence')
    ) {
      history.push('/reports/sequence');
      return;
    }

    if (
      leadFinderViewOnly() &&
      (!location.pathname.startsWith('/leads') || location.hash !== '#people')
    ) {
      history.push(`/leads${location.search}#people`);
      return;
    }

    if (prevProps.email !== email) {
      const agencyConfigPayload = {
        baseUrl: window.location.origin,
        email,
      };

      sendGetAgencyConfigRequest(agencyConfigPayload);
    }

    this.initializeHelpScoutChatSupport();

    const { isViewSequenceBtnClicked } = this.state;

    executeOnRequestStatusWithPrevStatusCheck({
      status: getUserSubscriptionMetadataRequestStatus,
      prevStatus: prevProps.getUserSubscriptionMetadataRequestStatus,
      onSuccess: () => {
        this.checkPaymentPendingBanner();
        resetGetUserSubscriptionMetadataRequest();
      },
      onFailed: () => {
        resetGetUserSubscriptionMetadataRequest();
        executeOnErrorWithErrorCheck({
          error: getUserSubscriptionMetadataRequestError,
          onError: () => {
            toaster.error(getUserSubscriptionMetadataRequestError.message);
          },
        });
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: getUserSettingsRequestStatus,
      prevStatus: prevProps.getUserSettingsRequestStatus,
      onSuccess: () => {
        const { willRedirectToLeadFinder } = this.state;

        // Re-identify user on page refresh for already-authenticated sessions.
        // On fresh login this is a no-op (user already identified in auth flow).
        if (trackingId) {
          initializeThirdPartyIntegrations({
            trackingId,
            firstName: firstName || '',
            lastName: lastName || '',
            email: email || '',
          });
        }

        if (
          !this.hasInitializedDialer &&
          isDialerFeatureEnabled &&
          hasPermission(Permissions.PHONE_CALL_READ)
        ) {
          this.hasInitializedDialer = true;
          if (!isDemoeAccount) {
            createTwillioVoiceTokenRequest(); // Create Twillio Voice Token for Dialer
          }
          sendMarkUnseenMissedCallCountRequest(); // Get Missed Calls Counts
          sendGetMyPhoneNumberListRequest({
            pageNumber: 1,
            pageSize: 25,
          });
        }

        storeUserEmail();
        sendGetPostLoadMetaRequest();
        sendGetUserSubscriptionMetadataRequest();
        // this.redirectToBlankPage();
        this.onCompleteOnboardingProcess();

        const customScriptForAgency = document.getElementById(
          'custom-script-for-agency',
        );
        if (
          shAccountId === 70803 &&
          client &&
          !isEmpty(client) &&
          !customScriptForAgency
        ) {
          initializeCustomScript();
        }

        if (willRedirectToLeadFinder) {
          redirectWithoutRefresh('/leads');
          this.setState({ willRedirectToLeadFinder: false });
        }

        if (hasUserConnectedEmailAccount) {
          this.updateConnectEmailProfileProgressStep();
          handleDemoFeedbackModal();
        }

        const params = new URLSearchParams(location.search);
        const action = params.get('action');

        if (action === 'extendTrial') {
          this.onTrialExtended();
        }

        // Version-based routing for Lead Finder (on initial load)
        const isOnOldLeadFinder =
          location.pathname === '/leads' ||
          location.pathname.startsWith('/leads#');
        const isOnNewLeadFinder = location.pathname.startsWith('/v2/leads');

        // Check v2 lead finder access after user settings are loaded
        if (!hasV2LeadFinderAccess() && isOnNewLeadFinder) {
          history.push('/leads');
          return;
        }

        // Only enforce version routing for version-locked users
        if (leadFinder?.isVersionLocked) {
          if (isOnOldLeadFinder && leadFinder.version === 'v2') {
            history.push('/v2/leads');
            return;
          }
          if (isOnNewLeadFinder && leadFinder.version === 'v1') {
            history.push('/leads');
            return;
          }
        }

        const demoAccountEntryPoint = getDemoAccountEntryPoint();
        if (isDemoAccount && demoAccountEntryPoint) {
          window.analytics?.track?.({
            userId: trackingId,
            event: AnalyticsEvents.DemoAccountEntered,
            properties: {
              shAccountId,
              entry_point: demoAccountEntryPoint,
            },
          });
          removeDemoAccountEntryPoint();
        }
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: outcomeChangeRequestStatus,
      prevStatus: prevProps.outcomeChangeRequestStatus,
      onSuccess: () => {
        toaster.success('Prospect outcome updated successfully', {
          theme: Theme.New,
        });
      },
      onFailed: () => {
        toaster.error(outcomeChangeRequestMessage, {
          theme: Theme.New,
        });
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: updateProfileProgressRequestStatus,
      prevStatus: prevProps.updateProfileProgressRequestStatus,
      onSuccess: () => {
        sendGetUserSettingsRequest();
        if (hasUserConnectedEmailAccount) {
          handleHasUserConnectedEmailAccount(false);
        }
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: resendVerificationEmailRequestStatus,
      prevStatus: prevProps.resendVerificationEmailRequestStatus,
      onSuccess: () => {
        toaster.success('Email resent successfully.', {
          theme: Theme.New,
        });
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: resendVerificationEmailRequestError,
          onError: () => {
            toaster.error(resendVerificationEmailRequestError.message, {
              theme: Theme.New,
            });
          },
        });
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: getAgencyConfigRequestStatus,
      prevStatus: prevProps.getAgencyConfigRequestStatus,
    });

    if (leadFinder?.isVersionLocked) {
      const isOnOldLeadFinder =
        location.pathname === '/leads' ||
        location.pathname.startsWith('/leads#');
      const isOnNewLeadFinder = location.pathname.startsWith('/v2/leads');

      if (isOnOldLeadFinder && leadFinder.version === 'v2') {
        history.push('/v2/leads');
        return;
      }
      if (isOnNewLeadFinder && leadFinder.version === 'v1') {
        history.push('/leads');
        return;
      }
    }

    if (agencyConfig?.colorPalette) {
      // Create Instance of the document element
      const documentInstance = document.documentElement;

      // Loop through the 'colorPalette' object
      Object.keys(agencyConfig.colorPalette).forEach((color) => {
        // Set CSS variables
        documentInstance.style.setProperty(
          `--${color}`,
          agencyConfig.colorPalette[color],
        );
      });
    }

    // Change the favicon

    executeOnRequestStatusWithPrevStatusCheck({
      status: createSequenceFromGettingStartedRequestStatus,
      prevStatus: prevProps.createSequenceFromGettingStartedRequestStatus,
      onSuccess: () => {
        redirectWithRefresh(
          `/sequence/${gettingStatedCreatedSequenceId}/steps`,
        );
        this.showHideWatchVideoModal(false);
        this.handleHelpscoutWelcomeMessage();
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: getSampleSequenceRequestStatus,
      prevStatus: prevProps.getSampleSequenceRequestStatus,
      onSuccess: () => {
        if (isGettingStartedVideoModalShow && isViewSequenceBtnClicked) {
          this.onViewSampleSequence();
        }
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: compareAuthTokenRequestStatus,
      prevStatus: prevProps.compareAuthTokenRequestStatus,
      onSuccess: () => {
        const params = new URLSearchParams(location.search);
        params.delete('authToken');
        if (shouldLogoutUser) {
          this.handleSessionExpire();
        } else {
          history.push(location.pathname);
        }
        resetCompareAuthTokenRequest();
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: extendTrialPeriodRequestStatus,
      prevStatus: prevProps.extendTrialPeriodRequestStatus,
      onSuccess: () => {
        toaster.success('Trial has been extended successfully', {
          theme: Theme.New,
        });
        redirectWithRefresh('/sequence');
      },
      onFailed: () => {
        if (extendTrialPeriodRequestMessage) {
          toaster.error(extendTrialPeriodRequestMessage, {
            theme: Theme.New,
          });
        }
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: onBoardingRequestStatus,
      prevStatus: prevProps.onBoardingRequestStatus,
      onSuccess: () => {
        const { willRedirectToLeadFinder } = this.state;

        this.showHideOnboardingModal(false);
        sendGetUserSettingsRequest();

        const searchParam = new URLSearchParams('');
        searchParam.set('onboarding', 'completed');

        if (willRedirectToLeadFinder) {
          redirectWithoutRefresh('/leads');
        } else {
          history.push({
            search: searchParam.toString(),
          });
        }
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: onBoardingRequestError,
          onError: () => {
            toaster.error(onBoardingRequestError.message, {
              theme: Theme.New,
            });
          },
        });
      },
    });

    if (window.Intercom) {
      window.Intercom('event', {
        type: HelpscoutBeaconEvents.PAGE_VIEWED,
        url: document.location.href,
        title: document.title,
      });
      window.Intercom(HelpscoutBeaconEvents.SUGGEST);
    }
    // this.initializeCelloReferral();
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }

    destroyChatSupport();
    removeCustomScript();

    window.removeEventListener('pagehide', this.handleTabClose);
  }

  onViewSampleSequence() {
    const {
      sampleSequence,
      sendGetSampleSequenceRequest,
      isMemberAccount,
      sendFinishOnboardingRequest,
    } = this.props;

    if (this.isUserAlreadyInStepPage()) {
      this.showHideWatchVideoModal(false);
      return;
    }

    if (isMemberAccount) {
      sendFinishOnboardingRequest();
    }

    if (sampleSequence) {
      const sequenceId = sampleSequence?.id;
      this.showHideWatchVideoModal(false);
      redirectWithoutRefresh(`/sequence/${sequenceId}/steps`);
      setTimeout(() => {
        window?.location?.reload();
      }, 2000);
    } else {
      this.setState({ isViewSequenceBtnClicked: true });
      sendGetSampleSequenceRequest();
    }
  }

  onCompleteOnboardingProcess() {
    const {
      meta,
      sendFinishOnboardingRequest,
      handleHasSignUpCompleted,
      sendGetSampleSequenceRequest,
      location,
      history,
      isMemberAccount,
      getAgencyConfigRequestStatus,
    } = this.props;
    const {
      showWatchVideoModal,
      showGettingStarted,
      willRedirectToLeadFinder,
    } = this.state;

    if (willRedirectToLeadFinder) {
      return;
    }

    if (!isEmpty(meta)) {
      const onboardingStep = meta.find(
        (setting) => setting.code === UserSettingCode.Onboarding,
      );

      if (onboardingStep?.value === OnboardingSteps.Step1) {
        if (isMemberAccount) {
          sendGetSampleSequenceRequest();
          // this.showHideWatchVideoModal(true);
          return;
        }

        this.showHideOnboardingModal(true);
        const currentPath = location.pathname + location.search;
        handleOnboarding(meta, currentPath, location.search);
      }

      if (onboardingStep?.value === OnboardingSteps.Step2) {
        sendFinishOnboardingRequest();
        handleHasSignUpCompleted(true);
        if (
          getIsRequestSucceeded(getAgencyConfigRequestStatus) &&
          !isWhitelabelWithClientView()
        ) {
          this.showHideWatchVideoModal(true);
        }
        this.showHideHelpscoutWelcomeMessage(true);

        const currentPath = location.pathname + location.search;
        handleOnboarding(meta, currentPath, 'videostarted');

        setTimeout(() => {
          history.push({
            search: '',
          });
        }, 2000);
      }

      const params = new URLSearchParams(location.search);
      if (
        params.get('redirect_to') === 'ltd-checkout' &&
        onboardingStep?.value === OnboardingSteps.Step0
      ) {
        history.push('/LTD-Checkout');
      }
    } else if (!showWatchVideoModal && !showGettingStarted) {
      this.showHideGettingStarted(true);
    }
  }

  onTrialExtended = () => {
    const {
      isEligibleToExtendTrial,
      sendExtendTrialPeriodRequest,
    } = this.props;

    if (isEligibleToExtendTrial) {
      sendExtendTrialPeriodRequest(TrialExtendSource.Email);
    }
  };

  getPlanTransitionDetails() {
    const { planTransitionDetails } = this.props;
    return planTransitionDetails;
  }

  handleTabClose = (event) => {
    if (!event.persisted) {
      const { trackingId, shAccountId } = this.props;

      window.analytics?.track?.({
        userId: trackingId,
        event: AnalyticsEvents.DemoEnded,
        properties: {
          shAccountId,
          reason: 'tab_closed',
          duration_seconds: getDemoAccountEnteredTimestamp(),
        },
      });
    }
  };

  updateWatchVideoProfileProgressStep = () => {
    const { profileProgress, updateProfileProgressRequest } = this.props;
    if (profileProgress) {
      const watchVideoStep = profileProgress.find(
        (step) =>
          step.profileProgressStep.stepName === ProfileProgressSteps.WatchVideo,
      );
      if (!watchVideoStep.isCompleted) {
        updateProfileProgressRequest({
          step: ProfileProgressSteps.WatchVideo,
          isCompleted: true,
        });
      }
    }
  };

  updateConnectEmailProfileProgressStep = () => {
    const { profileProgress, updateProfileProgressRequest } = this.props;
    if (profileProgress) {
      const connectEmailStep = profileProgress.find(
        (step) =>
          step.profileProgressStep.stepName ===
          ProfileProgressSteps.ConnectEmail,
      );
      if (!connectEmailStep.isCompleted) {
        updateProfileProgressRequest({
          step: ProfileProgressSteps.ConnectEmail,
          isCompleted: true,
        });
      }
    }
  };

  handleHelpscoutWelcomeMessage = () => {
    const { showHelpscoutWelcomeMessage } = this.state;

    window?.Beacon?.('show-message', process.env.REACT_APP_CFG_UI_02, {
      delay: 5000,
      force: showHelpscoutWelcomeMessage,
    });
    setTimeout(() => {
      this.showHideHelpscoutWelcomeMessage(false);
    }, 6000);
  };

  initializeHelpScoutChatSupport = () => {
    const { subscriptionDetails, icpStatus, location } = this.props;

    if (
      window?.location &&
      (subscriptionDetails || icpStatus) &&
      hasPermission(Permissions.CHAT_SUPPORT_SHOW)
    ) {
      initializeIntercom();

      setTimeout(() => {
        const params = new URLSearchParams(location.search);
        const chatOpen = params.get('chatopen');
        if (chatOpen === 'true') {
          openIntercomHome();
        }
      }, 1000);
    }
  };

  // initializeCelloReferral = () => {
  //   initializeCello();
  // };

  handleSendVerificationLinkRequest() {
    const { resendVerificationEmailRequest, email } = this.props;

    resendVerificationEmailRequest(email);
  }

  checkPaymentPendingBanner() {
    const {
      subscriptionMetadata = [],
      setPageHeaderBannerAlert,
      hasExpiringOrders = false,
      t,
    } = this.props;

    const {
      paymentActionRequired,
      paymentActionType,
      paymentActionUrl,
      bannerMessage,
    } = handlePaymentAction(subscriptionMetadata);

    if (paymentActionRequired) {
      if (hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)) {
        if (paymentActionType === PaymentActionType.Initial) {
          setPageHeaderBannerAlert({
            bannerType: PageHeaderAlertTypes.PAYMENT_PENDING,
            paymentActionUrl,
            bannerMessageOne: t(`messages.${bannerMessage}`),
            bannerMessageTwo: t('messages.initial_payment_pending_text_two'),
            ctaText: t('labels.take_action_now_cta'),
            paymentActionType,
          });
        } else {
          setPageHeaderBannerAlert({
            bannerType: PageHeaderAlertTypes.PAYMENT_PENDING,
            paymentActionUrl,
            bannerMessageOne: t(`messages.${bannerMessage}`),
            bannerMessageTwo: t('messages.recurring_payment_pending_text_two'),
            ctaText: t('labels.take_action_now_cta'),
            paymentActionType,
          });
        }
      }
    } else if (hasExpiringOrders) {
      if (hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)) {
        setPageHeaderBannerAlert({
          bannerType: PageHeaderAlertTypes.DOMAIN_RENEWAL,
          message: t('messages.domain_renewal_required'),
          ctaText: t('labels.renew_now_cta'),
          ctaUrl: Routes.Setting_Billing_Sending_Infrastructure,
          ctaHandlerType: 'redirect',
        });
      } else {
        setPageHeaderBannerAlert({
          bannerType: PageHeaderAlertTypes.DOMAIN_RENEWAL,
          message: t('messages.domain_renewal_required'),
          ctaHandlerType: 'contact-owner',
          ctaBtnText: t('labels.contact_owner_cta'),
        });
      }
    } else {
      this.checkSubscriptionRenewalBanner();
    }
  }

  checkSubscriptionRenewalBanner() {
    const { setPageHeaderBannerAlert, t } = this.props;

    getSubscriptionRenewalStatus()
      .then((renewalStatus) => {
        if (
          renewalStatus.hasActiveRenewal &&
          renewalStatus.renewals.length > 0
        ) {
          const firstRenewal = renewalStatus.renewals[0];
          const deadlineDate = new Date(
            firstRenewal.billingPeriodEndAt,
          ).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
          if (hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)) {
            setPageHeaderBannerAlert({
              bannerType: PageHeaderAlertTypes.SUBSCRIPTION_RENEWAL,
              message: t('messages.subscription_renewal_required', {
                date: deadlineDate,
              }),
              ctaText: t('labels.renew_now_cta'),
              ctaUrl: `${Routes.Email_Infra_Pay}?orderId=${firstRenewal.orderId}`,
              ctaHandlerType: 'redirect',
            });
          } else {
            setPageHeaderBannerAlert({
              bannerType: PageHeaderAlertTypes.SUBSCRIPTION_RENEWAL,
              message: t('messages.subscription_renewal_required', {
                date: deadlineDate,
              }),
              ctaHandlerType: 'contact-owner',
              ctaBtnText: t('labels.contact_owner_cta'),
            });
          }
        } else {
          setPageHeaderBannerAlert({
            bannerType: PageHeaderAlertTypes.NONE,
          });
        }
      })
      .catch(() => {
        setPageHeaderBannerAlert({
          bannerType: PageHeaderAlertTypes.NONE,
        });
      });
  }

  redirectToBlankPage() {
    const { planTransitionDetails, history } = this.props;

    if (isPlanDowngraded(planTransitionDetails)) {
      history.push('/block');
    }
  }

  isUserAlreadyInStepPage() {
    const { sampleSequence, match } = this.props;
    const { params }: any = match;

    if (params) {
      return sampleSequence?.id === +params?.sequenceId;
    }

    return false;
  }

  showHideGettingStarted(value: boolean) {
    this.setState({ showGettingStarted: value });
  }

  showHideWatchVideoModal(value: boolean) {
    const { showGettingStartedVideoModal } = this.props;

    this.setState({ showWatchVideoModal: value });
    showGettingStartedVideoModal(value);
  }

  showHideHelpscoutWelcomeMessage(value: boolean) {
    this.setState({ showHelpscoutWelcomeMessage: value });
  }

  showHideOnboardingModal(value: boolean) {
    this.setState({ showOnboardingModal: value });
  }

  createSequence(source: Source) {
    const { sendCreateSequenceRequest } = this.props;
    if (!hasPermission(Permissions.SEQUENCE_WRITE)) {
      if (hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)) {
        planError(2002);
      } else {
        planError(3002);
      }
    } else if (!hasResource(ResourceIdentifiers.SEQUENCES_CREATE)) {
      this.showHideWatchVideoModal(false);
    } else {
      const { userTimeZone } = this.props;
      sendCreateSequenceRequest(getNewSequenceName(userTimeZone), source);
    }
  }

  handleSessionExpire = () => {
    this.setState({ sessionExpire: true });
  };

  handleSessionExpireLogin = () => {
    const { location, isDemoAccount, trackingId, shAccountId } = this.props;

    this.setState({ sessionExpire: false });

    if (isDemoAccount) {
      window.analytics?.track?.({
        userId: trackingId,
        event: AnalyticsEvents.DemoEnded,
        properties: {
          shAccountId,
          reason: 'timeout',
          duration_seconds: getDemoAccountEnteredTimestamp(),
        },
      });
      AuthHelper.logoutDemoAccount();
    } else {
      AuthHelper.logout({
        fallbackTo: location,
        redirect: true,
        pathName: getAgencyClientRedirectURLOnLogout() || '/login',
      });
    }

    const interval = setInterval(() => {
      clearAgencyClientRedirectURLOnLogout();
      clearInterval(interval);
    }, 1000);
  };

  render() {
    const {
      getUserSettingsRequestStatus,
      meta,
      subscriptionDetails,
      firstName,
      isGettingStartedVideoModalShow,
      getSampleSequenceRequestStatus,
      createSequenceFromGettingStartedRequestStatus,
      sendOnboardingRequest,
      verified,
      onBoardingRequestStatus,
      trackingId,
      userId,
      history,
      isMemberAccount,
      sendFinishOnboardingRequest,
      pageHeaderBannerMessage,
      sendExploreDemoAccountRequest,
      setDemoAccountEntryPoint,
      isDemoAccount,
      showDemoFeedbackModal,
      handleShowDemoFeedbackModal,
      exploreDemoAccountRequestStatus,
    } = this.props;

    const {
      userSubscriptionPlan,
      showOnboardingModal,
      showWatchVideoModal,
      sessionExpire,
    } = this.state;

    const location = history?.location;
    const isLoading =
      getUserSettingsRequestStatus === RequestStatus.Pending ||
      getUserSettingsRequestStatus === RequestStatus.Ideal;

    if (isLoading && isEmpty(meta) && !subscriptionDetails) {
      return null;
    }

    // Classes
    const homeContainerClass = classNames([
      'flex-1',
      {
        'home-container': userSubscriptionPlan !== SubscriptionPlans.FREE,
      },
      {
        'home-container show-alert':
          userSubscriptionPlan === SubscriptionPlans.FREE,
      },
      {
        'home-container-sidenav-hidden':
          location?.pathname?.includes(
            Routes.Setting_Billing_Upgrade_Lead_Finder_Plan,
          ) ||
          location?.pathname?.includes(
            Routes.Setting_Billing_Upgrade_Email_Outreach_Plan,
          ) ||
          location?.pathname?.includes(
            Routes.Setting_Billing_Inbox_Placement_Radar,
          ) ||
          location?.pathname?.includes(
            Routes.Setting_Billing_Upgrade_Dialer_Plan,
          ),
      },
    ]);

    return (
      <Container
        fluid
        className={`p-0 ${
          !verified || pageHeaderBannerMessage !== '' || isDemoAccount
            ? 'alert-message'
            : ''
        }`}
      >
        <HelmetIcon />

        <ErrorBoundaryWrapper>
          <ThemeProvider prefixes={{ btn: 'btn-solid' }}>
            <Row className="m-0 flex-nowrap position-relative">
              <Sidebar />
              <main className={homeContainerClass}>
                {isDemoAccount && <DemoAccountBanner />}
                {!verified && (
                  <Alert
                    className="email-verify-alert"
                    contentContainer={
                      <span>
                        Check your registered email and authenticate your
                        profile by clicking on the link. If you have not
                        received it,{' '}
                        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus, jsx-a11y/click-events-have-key-events */}
                        <span role="button" onClick={this.redirectSpamUrl}>
                          check spam folder
                        </span>
                        {' / '}
                        <span
                          {...accessibleOnClick(
                            this.handleSendVerificationLinkRequest,
                          )}
                        >
                          send again
                        </span>
                        .
                      </span>
                    }
                    variant={Alert.Variant.Warning}
                    theme={Alert.Theme.New}
                  />
                )}
                <Header />
                <DialerManager />
                <Switch>
                  <ConfigRoute
                    exact
                    path="/v2/leads"
                    title="Leads"
                    component={() => {
                      // Preserve search params when redirecting to default tab (Company is first tab)
                      history.replace(
                        `/v2/leads/company/total${location.search}${location.hash}`,
                      );
                      return null;
                    }}
                  />
                  <ConfigRoute
                    path="/v2/leads/csv-enrichment"
                    title="CSV Enrichment"
                    component={V2Lead}
                  />
                  <ConfigRoute
                    path="/v2/leads/:tab/:subtab"
                    title="Leads"
                    component={V2Lead}
                  />
                  <ConfigRoute
                    path="/leads"
                    title="Leads"
                    component={
                      process.env
                        .REACT_APP_SHOW_LEAD_FINDER_MAINTAINANCE_PAGE === 'true'
                        ? MaintenancePage
                        : leads
                    }
                  />
                  <ConfigRoute
                    path="/sequence"
                    title="Sequence"
                    component={Sequence}
                  />
                  <ConfigRoute path="/tasks" title="Tasks" component={Tasks} />
                  <ConfigRoute
                    path="/conversations"
                    title="Unified Inbox"
                    component={UnifiedInbox}
                  />
                  <ConfigRoute
                    path="/clients"
                    title="Client Management"
                    component={AgencyClient}
                  />
                  <ConfigRoute
                    path="/billing"
                    title="Billings"
                    component={Billing}
                  />
                  <ConfigRoute
                    path="/settings"
                    title="Settings"
                    component={Setting}
                  />
                  <ConfigRoute // deprecated route Do not remove it
                    path="/prospects"
                    title="Prospects"
                    component={Prospect}
                  />
                  <ConfigRoute // new route, use this route instead of /prospects
                    path="/crm"
                    title="CRM"
                    component={Prospect}
                  />
                  <ConfigRoute
                    path="/dialer"
                    title="Dialer"
                    component={Dialer}
                  />
                  <ConfigRoute
                    path="/email-accounts"
                    title="Email-Accounts"
                    component={EmailAccounts}
                  />
                  <ConfigRoute
                    path="/domains"
                    title="Domains"
                    component={Domains}
                  />
                  <ConfigRoute
                    path="/inframail-ips"
                    title="Inframail Ips"
                    component={InframailIps}
                  />
                  <ConfigRoute
                    path="/templates"
                    title="Template"
                    component={Templates}
                  />
                  <ConfigRoute
                    path="/email-insights"
                    title="1:1 Email Insights"
                    component={MailboxEmails}
                  />
                  <ConfigRoute
                    path="/inbox-radar"
                    title="Inbox Radar"
                    component={inboxRadar}
                  />
                  <ConfigRoute
                    path="/reports"
                    title="Reports"
                    component={Reports}
                  />
                  <ConfigRoute
                    path="/growth-hub"
                    title="Growth Hub"
                    component={growthHub}
                  />
                  <ConfigRoute
                    path="/agency-portal"
                    title="Agency Portal"
                    component={AgencyPortal}
                  />
                  <ConfigRoute
                    path="/warmup"
                    title="Email Warmup"
                    component={EmailWarmup}
                  />
                  <ConfigRoute
                    path="/verify-email"
                    title="Verify Email"
                    component={VerifyEmailCallback}
                  />
                  <ConfigRoute
                    path="/block"
                    title="Block"
                    component={BlockPage}
                  />
                  <ConfigRoute
                    path="/email-verifier"
                    title="Email Verifier"
                    component={EmailVerifier}
                  />
                </Switch>

                {/* {agencyConfig?.showGettingStarted && (
                  <GettingStartedOnboarding
                    firstName={firstName}
                    profileProgress={profileProgress}
                    showHideWatchVideoModal={this.showHideWatchVideoModal}
                    handleActivateSequenceTooltip={
                      handleActivateSequenceTooltip
                    }
                    handleAddEmailAccountModal={handleAddEmailAccountModal}
                    createSequence={() =>
                      this.createSequence(Source.OnboardingChecklist)
                    }
                    showGettingStarted={showGettingStarted}
                  />
                )} */}

                {isGettingStartedVideoModalShow &&
                  isWhitelabelWithoutClientView() && (
                    <WatchVideoModal
                      show={isGettingStartedVideoModalShow}
                      isGettingStartedVideModalShowThroughIcon={
                        showWatchVideoModal
                      }
                      firstName={firstName}
                      onViewSampleSequence={this.onViewSampleSequence}
                      onCreateNewSequence={() =>
                        this.createSequence(Source.OnboardingVideo)
                      }
                      onClose={() => {
                        if (isMemberAccount) {
                          sendFinishOnboardingRequest();
                          setTimeout(() => {
                            window?.location?.reload?.();
                          }, 1000);
                          return this.showHideWatchVideoModal(false);
                        }

                        this.showHideWatchVideoModal(false);

                        if (
                          getCurrentRoute(location) !== '/settings/whitelabel'
                        ) {
                          this.showHideOnboardingModal(false);
                          this.handleHelpscoutWelcomeMessage();
                          this.setState({ isViewSequenceBtnClicked: false });
                          history.push({
                            search: 'videoclosed',
                          });
                          // window.location.reload();
                          setTimeout(() => {
                            history.push({
                              search: '',
                            });
                          }, 2000);
                          this.showHideWatchVideoModal(false);
                        }
                      }}
                      updateWatchVideoProfileProgressStep={
                        this.updateWatchVideoProfileProgressStep
                      }
                      isCreateSequenceRequestPending={getIsRequestPending(
                        createSequenceFromGettingStartedRequestStatus,
                      )}
                      isViewSampleSequenceRequestPending={getIsRequestPending(
                        getSampleSequenceRequestStatus,
                      )}
                      sendExploreDemoAccountRequest={() => {
                        setDemoAccountEntryPoint('onboarding_screen');
                        sendExploreDemoAccountRequest();
                      }}
                      exploreDemoAccountRequestStatusPending={getIsRequestPending(
                        exploreDemoAccountRequestStatus,
                      )}
                    />
                  )}

                <OnboardingModal
                  show={showOnboardingModal}
                  firstName={firstName}
                  sendOnboardingRequest={sendOnboardingRequest}
                  onBoardingRequestStatus={onBoardingRequestStatus}
                  trackingId={trackingId}
                  shAccountId={userId}
                  checkIsLeadFinderICP={(s: boolean) =>
                    this.setState({
                      willRedirectToLeadFinder: s,
                    })
                  }
                />

                <SessionExpireModal
                  show={sessionExpire}
                  showCloseIcon={false}
                  onHide={() => {}}
                  onSubmit={this.handleSessionExpireLogin}
                  submitButtonText="Login"
                />

                <FeedbackModal
                  show={showDemoFeedbackModal}
                  onClose={() => {
                    handleShowDemoFeedbackModal(false);
                  }}
                />
              </main>
            </Row>
            <ThirdPartyIntegrations />
          </ThemeProvider>
        </ErrorBoundaryWrapper>
      </Container>
    );
  }
}

export default Home;
